import React, { useState } from 'react';
import {
  Button,
  SegmentedControl,
  Switch,
  Typography,
  TypographyType,
  TypographyVariant,
} from 'src/components-dummy';
import { ControlSnapshotPanel } from '../ControlSnapshotPanel';
import { BlastRadiusBanner } from '../BlastRadiusBanner';
import { FullConfigDiffModal } from '../FullConfigDiffModal';
import { useViewConfigEditor } from './useViewConfigEditor';
import { ViewConfigEditorProps } from './types';
import './ViewConfigEditor.scss';

export const ViewConfigEditor = (props: ViewConfigEditorProps): JSX.Element => {
  const {
    isLoading,
    hasError,
    isSupported,
    resultsCountOptions,
    control,
    test,
    controlItems,
    changeCount,
    surfaceLabel,
    setResultsCount,
    setAddToCartActive,
  } = useViewConfigEditor(props);
  const { testVariant } = props;
  const [isFullDiffOpen, setIsFullDiffOpen] = useState(false);
  const changedEntities = testVariant?.versionChangesSummary?.changedEntities;

  if (!isSupported) {
    return (
      <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
        View config changes are not available for this surface yet.
      </Typography>
    );
  }

  if (hasError) {
    return (
      <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
        Couldn&apos;t load view config for this surface. This can happen if the catalogue has no
        items yet — add items in Catalogue Manager, then come back to this step.
      </Typography>
    );
  }

  if (isLoading || !control || !test) {
    return (
      <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
        Loading view config...
      </Typography>
    );
  }

  return (
    <div className='syte-view-config-editor'>
      <ControlSnapshotPanel items={controlItems} />

      <div className='syte-view-config-editor-variant'>
        <div className='syte-view-config-editor-variant-header'>
          <span className='syte-view-config-editor-variant-dot' />
          <Typography type={TypographyType.Body} variant={TypographyVariant.MediumBold}>
            {`Variant B · View config on ${surfaceLabel}`}
          </Typography>
          <span className='syte-view-config-editor-variant-count'>
            {`${changeCount} change${changeCount === 1 ? '' : 's'}`}
          </span>
        </div>

        <table className='syte-view-config-editor-table'>
          <thead>
            <tr>
              <th>Setting</th>
              <th>Control</th>
              <th>{`Variant B`}</th>
              <th>Change</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <div className='syte-view-config-editor-table-name'>Results shown</div>
              </td>
              <td className='syte-view-config-editor-table-control'>
                {control.numberOfResultsToDisplay}
              </td>
              <td>
                <SegmentedControl
                  options={resultsCountOptions.map(option => ({
                    value: String(option),
                    label: String(option),
                  }))}
                  value={String(test.numberOfResultsToDisplay)}
                  onChange={value => setResultsCount(Number(value))}
                />
              </td>
              <td className='syte-view-config-editor-table-change'>
                {control.numberOfResultsToDisplay === test.numberOfResultsToDisplay
                  ? '—'
                  : `${control.numberOfResultsToDisplay} → ${test.numberOfResultsToDisplay}`}
              </td>
            </tr>
            <tr>
              <td>
                <div className='syte-view-config-editor-table-name'>Add to cart on carousel</div>
              </td>
              <td className='syte-view-config-editor-table-control'>
                {control.addToCartActive ? 'On' : 'Off'}
              </td>
              <td>
                <Switch
                  checked={test.addToCartActive}
                  onChange={checked => setAddToCartActive(checked)}
                />
              </td>
              <td className='syte-view-config-editor-table-change'>
                {control.addToCartActive === test.addToCartActive
                  ? '—'
                  : `${control.addToCartActive ? 'On' : 'Off'} → ${test.addToCartActive ? 'On' : 'Off'}`}
              </td>
            </tr>
          </tbody>
        </table>

        <div className='syte-view-config-editor-actions'>
          <Button
            prefixId='variantsStep_viewConfig_fullConfigDiff'
            variant='tertiary'
            onClick={() => setIsFullDiffOpen(true)}
            disabled={!changedEntities}
          >
            Full config diff
          </Button>
          <Typography
            type={TypographyType.Body}
            variant={TypographyVariant.SmallRegular}
            className='syte-view-config-editor-actions-caption'
          >
            Every other setting stays identical to control.
          </Typography>
        </div>

        {changeCount > 1 && (
          <BlastRadiusBanner changeCount={changeCount} scopeLabel={surfaceLabel} />
        )}

        {/* TODO: results preview (top 5 for a sample product) — implement later */}
      </div>

      {isFullDiffOpen && changedEntities && (
        <FullConfigDiffModal
          isOpen={isFullDiffOpen}
          onClose={() => setIsFullDiffOpen(false)}
          changedEntities={changedEntities}
          variantName={testVariant?.name}
        />
      )}
    </div>
  );
};
